import {Injectable, inject} from '@angular/core'
import {Actions, createEffect, ofType} from '@ngrx/effects'
import {Store} from '@ngrx/store'
import moment from 'moment'
import {forkJoin, of} from 'rxjs'
import {filter, map, mergeMap, switchMap, withLatestFrom} from 'rxjs/operators'
import {setSplashStatus} from '@core/store/core.actions'
import {Appointment} from '@models/appointment'
import {ClassifierSection} from '@models/classifier'
import {GroupCustomer} from '@models/group-customer'
import {Outlet} from '@models/outlet'
import {RangeDate} from '@models/schedule'
import {AppointmentService} from '@service/appointment.service'
import {AuthService} from '@service/auth/auth.service'
import {GpsService} from '@service/gps.service'
import {GroupService} from '@service/group.service'
import {InfoService} from '@service/info/info.service'
import {OutletService} from '@service/outlet/outlet.service'
import {PermissionsService} from '@service/permissions/permissions.service'
import {
  HomeActionsTypes,
  filterOutletsResponse,
  getClassifiersResponse,
  getOutletListPaginateResponse,
  getOutletListResponse,
} from './home.actions'
import {
  getOutletLoadedSelector,
  getOutletsFiltersSelector,
  getOutletsPaginationSelector,
  getStaticOutletsPlannedSelector,
} from './home.selectors'
import {HomePageService} from '../services/home-page.service'

@Injectable()
export class HomeEffects {
  private readonly actions$ = inject(Actions)
  private readonly store = inject(Store)
  private readonly infoService = inject(InfoService)
  private readonly groupService = inject(GroupService)
  private readonly appointmentService = inject(AppointmentService)
  private readonly outletService = inject(OutletService)
  private readonly authService = inject(AuthService)
  private readonly permissionsService = inject(PermissionsService)
  private readonly gpsService = inject(GpsService)
  private readonly homePageService = inject(HomePageService)

  classifiers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActionsTypes.GET_CLASSIFIERS_REQUEST),
      switchMap(() => this.infoService.getClassifiers()),
      map(
        (classifiers: {
          orders: ClassifierSection[]
          customers: ClassifierSection[]
          orderLine: ClassifierSection[]
          products: ClassifierSection[]
        }) => getClassifiersResponse({classifiers})
      )
    )
  )

  outletsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActionsTypes.GET_LIST_REQUEST),
      withLatestFrom(this.store.select(getOutletLoadedSelector)),
      filter(([, outletsLoaded]) => !outletsLoaded),
      withLatestFrom(this.store.select(getOutletsPaginationSelector)),
      switchMap(([, {pageSize, pageNumber}]) =>
        forkJoin({
          groups: this.permissionsService.canReadRoutes ? this.groupService.getTodayGroups() : of([]),
          appointments: this.appointmentService.getAppointments(moment(), RangeDate.DAY),
          pageSize: of(pageSize),
          pageNumber: of(pageNumber),
        })
      ),
      switchMap(
        ({
          groups,
          appointments,
          pageSize,
          pageNumber,
        }: {
          groups: GroupCustomer[]
          appointments: Appointment[]
          pageSize: number
          pageNumber: number
        }) => {
          const ids = [...groups.map(e => e.id), ...appointments.map(e => e.targetId)]
          return forkJoin([
            this.outletService.getBulkOutlets(ids, this.authService.uveUser.userId.toString(), groups, appointments),
            this.outletService.getPaginatedNotPlannedOutlets({
              pageSize,
              pageNumber,
              orderBy: this.outletService.appliedOrder,
              orderDirection: this.outletService.appliedOrderDirection,
              exclude: ids,
            }),
            of(groups),
            of(appointments),
          ])
        }
      ),
      switchMap(async data => {
        await this.gpsService.refreshPosition({waitingToFinish: false})

        const planned = data[0]
        const groups = data[2]
        const appointments = data[3]

        const newPlannedFromGroup: Outlet[] = await Promise.all(
          groups
            .filter(gr => planned.some(o => o.id?.toString() === gr.id?.toString()))
            .map(async e => {
              const outlet = planned.find(o => o.id?.toString() === e.id?.toString())
              return this.outletService.mapOutlet(
                {
                  ...outlet,
                  appointmentDate: null,
                  fromGroup: null,
                },
                {
                  group: e,
                  appointment: null,
                }
              )
            })
        )

        const newPlannedFromAppointment = await Promise.all(
          appointments
            .filter(ap => planned.some(o => o.id?.toString() === ap.targetId?.toString()))
            .map(async e => {
              const outlet = planned.find(o => o.id?.toString() === e.targetId?.toString())

              return this.outletService.mapOutlet(
                {
                  ...outlet,
                  appointmentDate: null,
                  fromGroup: null,
                },
                {
                  group: null,
                  appointment: e,
                }
              )
            })
        )
        const newPlanned = [...newPlannedFromGroup, ...newPlannedFromAppointment]
        return [...data, newPlanned]
      }),
      mergeMap(
        ([planned, [notPlanned, notPlannedPaginated, total], groups, appointments, newPlanned]: [
          Outlet[],
          [Outlet[], Outlet[], number],
          GroupCustomer[],
          Appointment[],
          Outlet[],
        ]) => {
          this.outletService.allOutletFiltered$ = notPlanned.concat(planned)

          return [
            getOutletListResponse({
              allOutletFiltered: notPlanned.concat(planned),
              planned: newPlanned,
              notPlanned: notPlannedPaginated,
              total,
              outletsLoaded: true,
            }),
            setSplashStatus({splashStatus: false}),
          ]
        }
      )
    )
  )

  filter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActionsTypes.FILTER_OUTLETS_REQUEST),
      //debounceTime(200),
      withLatestFrom(
        this.store.select(getOutletsPaginationSelector),
        this.store.select(getOutletsFiltersSelector),
        this.store.select(getStaticOutletsPlannedSelector)
      ),
      switchMap(async ([, {pageSize, pageNumber}, filters, planned]) => {
        this.homePageService.appliedFilters = filters

        return Promise.all([
          this.outletService.getPaginatedNotPlannedOutlets({
            pageSize,
            pageNumber,
            filters,
            orderBy: this.outletService.appliedOrder,
            orderDirection: this.outletService.appliedOrderDirection,
            exclude: planned.map(p => Number(p.id)),
          }),
          this.outletService.reloadOutletDistances(planned),
        ])
      }),
      map(([[allOutlets, notPlannedPaginated, total], planned]: [[Outlet[], Outlet[], number], Outlet[]]) => {
        this.outletService.allFilteredOutletsMap$ = allOutlets

        return filterOutletsResponse({
          allFilteredOutletsMap: allOutlets,
          notPlanned: notPlannedPaginated,
          total,
          planned,
        })
      })
    )
  )

  pagination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActionsTypes.GET_LIST_PAGINATE_REQUEST),
      //debounceTime(200),
      withLatestFrom(
        this.store.select(getOutletsPaginationSelector),
        this.store.select(getOutletsFiltersSelector),
        this.store.select(getStaticOutletsPlannedSelector)
      ),
      switchMap(([, pagination, filters, planned]) =>
        this.outletService.getPaginatedNotPlannedOutlets({
          ...pagination,
          filters,
          orderBy: this.outletService.appliedOrder,
          orderDirection: this.outletService.appliedOrderDirection,
          exclude: planned.map(p => Number(p.id)),
        })
      ),
      map(([, notPlanned]) => getOutletListPaginateResponse({notPlanned}))
    )
  )
}
